/* ==========================================================================
   CORDYGEN — theme.js
   Vanilla JS, no dependencies. Loaded with `defer`.
   ========================================================================== */
(() => {
  'use strict';

  const C = window.Cordygen || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (s, el = document) => el.querySelector(s);
  const qsa = (s, el = document) => Array.from(el.querySelectorAll(s));
  const debounce = (fn, wait = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const toast = (msg) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  /* ---------- Focus trap helpers ---------- */
  const trapFocus = (container, e) => {
    const f = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ==========================================================================
     Drawer base (menu + cart)
     ========================================================================== */
  class CgDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') trapFocus(this.panel, e);
      });
    }
    get panel() { return qs('.drawer__panel', this); }
    open(opener) {
      this._opener = opener || document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      if (this._opener && this._opener.setAttribute) this._opener.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => (qs('[data-close].icon-btn', this) || this.panel).focus({ preventScroll: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (this._opener && this._opener.setAttribute) {
        this._opener.setAttribute('aria-expanded', 'false');
        this._opener.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('menu-drawer', class extends CgDrawer {});

  /* ==========================================================================
     Cart
     ========================================================================== */
  const Cart = {
    get drawer() { return qs('cart-drawer'); },
    sectionsToRender() {
      const ids = [];
      if (this.drawer) ids.push('cart-drawer');
      const page = qs('cart-items[data-context="page"]');
      if (page) ids.push(page.closest('[id^="shopify-section-"]').id.replace('shopify-section-', ''));
      return ids;
    },
    updateCount(count) {
      qsa('[data-cart-count-badge]').forEach((b) => {
        b.hidden = count === 0;
        const n = b.querySelector('[aria-hidden]');
        if (n) n.textContent = count;
      });
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer' && this.drawer) {
          const fresh = qs('cart-drawer', doc);
          if (!fresh) return;
          const panel = qs('.drawer__panel', this.drawer);
          panel.innerHTML = qs('.drawer__panel', fresh).innerHTML;
          this.drawer.dataset.cartCount = fresh.dataset.cartCount;
          this.updateCount(parseInt(fresh.dataset.cartCount, 10) || 0);
        } else {
          const target = document.getElementById('shopify-section-' + id);
          const src = doc.getElementById('shopify-section-' + id);
          if (target && src) target.innerHTML = src.innerHTML;
        }
      });
    },
    async add(formData) {
      const ids = this.sectionsToRender();
      if (ids.length) { formData.append('sections', ids.join(',')); formData.append('sections_url', window.location.pathname); }
      const res = await fetch(C.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || C.strings.cartError);
      return data;
    },
    async change(line, quantity) {
      const ids = this.sectionsToRender();
      const res = await fetch(C.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ids, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || C.strings.cartError);
      this.render(data.sections);
      this.updateCount(data.item_count);
      return data;
    },
    afterAdd(data, opener) {
      if (C.cartType === 'page' || !this.drawer) { window.location.href = C.routes.cart; return; }
      this.render(data.sections);
      this.drawer.open(opener);
    }
  };
  window.CordygenCart = Cart;

  customElements.define('cart-drawer', class extends CgDrawer {});

  document.addEventListener('click', (e) => {
    const cartBtn = e.target.closest('[data-open-cart]');
    if (cartBtn && C.cartType === 'drawer' && Cart.drawer) { e.preventDefault(); Cart.drawer.open(cartBtn); return; }
    const drawerBtn = e.target.closest('[data-open-drawer]');
    if (drawerBtn) { const d = document.getElementById(drawerBtn.dataset.openDrawer); if (d) d.open(drawerBtn); return; }
    const searchBtn = e.target.closest('[data-open-search]');
    if (searchBtn) { const m = qs('search-modal'); if (m) m.open(searchBtn); }
  });

  /* Quantity input */
  customElements.define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const step = btn.name === 'plus' ? 1 : -1;
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  });

  /* Cart items (drawer + page) */
  document.addEventListener('change', debounce(async (e) => {
    const input = e.target.closest('cart-items input[data-line]');
    if (!input) return;
    const container = input.closest('cart-items');
    container.style.opacity = '.55';
    try { await Cart.change(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0); }
    catch (err) { toast(err.message); }
    finally { container.style.opacity = ''; }
  }, 300));

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-remove]');
    if (!rm) return;
    e.preventDefault();
    try { await Cart.change(parseInt(rm.dataset.remove, 10), 0); } catch (err) { toast(err.message); }
  });

  document.addEventListener('input', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(C.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 500));

  /* ==========================================================================
     Product form (main + quick add)
     ========================================================================== */
  const submitAdd = async (form, button) => {
    const err = form.closest('product-form') ? qs('[data-error]', form.closest('product-form')) : null;
    if (err) err.hidden = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      const data = await Cart.add(new FormData(form));
      Cart.afterAdd(data, button);
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; } else { toast(ex.message); }
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  customElements.define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = qs('[data-add-button]', this.form);
        if (btn.disabled) return;
        submitAdd(this.form, btn);
      });
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form.js-quick-add');
    if (!form) return;
    e.preventDefault();
    submitAdd(form, qs('button[type="submit"]', form));
  });

  /* ==========================================================================
     Variant picker
     ========================================================================== */
  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(qs('[data-variants]', this).textContent);
      this.info = this.closest('product-info');
      this.addEventListener('change', () => this.onChange());
    }
    onChange() {
      const selected = qsa('fieldset', this).map((fs) => {
        const c = qs('input:checked', fs);
        const label = qs('[data-selected-value]', fs);
        if (label && c) label.textContent = c.value;
        return c ? c.value : null;
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const form = document.getElementById(this.info.dataset.formId);
      const btn = form && qs('[data-add-button]', form);
      const label = btn && qs('[data-add-label]', btn);
      const sticky = qs('sticky-atc');
      const stickyBtn = sticky && qs('[data-sticky-add]', sticky);

      if (!variant) {
        if (btn) { btn.disabled = true; label.textContent = C.strings.unavailable; }
        if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = C.strings.unavailable; }
        return;
      }
      qs('[data-variant-id]', form).value = variant.id;
      btn.disabled = !variant.available;
      label.textContent = variant.available ? C.strings.addToCart : C.strings.soldOut;
      if (stickyBtn) { stickyBtn.disabled = !variant.available; stickyBtn.textContent = label.textContent; }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.featured_media) {
        const gallery = qs('media-gallery', this.info);
        if (gallery) gallery.goToMedia(variant.featured_media.id);
      }

      const section = this.info.closest('[data-product-url]');
      fetch(`${section.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.section}`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-price-wrap]', '[data-stock]'].forEach((sel) => {
            const a = qs(sel, this.info), b = qs(sel, doc);
            if (a && b) a.replaceWith(b);
          });
          const sp = sticky && qs('[data-sticky-price]', sticky);
          const np = qs('[data-sticky-price]', doc);
          if (sp && np) sp.textContent = np.textContent;
        })
        .catch(() => {});
    }
  });

  /* ==========================================================================
     Media gallery + lightbox
     ========================================================================== */
  customElements.define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.viewport = qs('[data-viewport]', this);
      this.slides = qsa('.gallery__slide', this);
      this.index = 0;
      if (this.slides.length < 2) { this.bindZoom(); return; }
      this.viewport.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go) { this.go(parseInt(go.dataset.go, 10)); return; }
        if (e.target.closest('[data-prev]')) this.go(this.index - 1);
        if (e.target.closest('[data-next]')) this.go(this.index + 1);
      });
      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });
      this.bindZoom();
      this.sync();
    }
    go(i) {
      const n = Math.max(0, Math.min(this.slides.length - 1, i));
      this.viewport.scrollTo({ left: this.slides[n].offsetLeft - this.viewport.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.index = n;
      this.update();
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i);
    }
    sync() {
      const w = this.viewport.clientWidth || 1;
      this.index = Math.round(this.viewport.scrollLeft / w);
      this.update();
    }
    update() {
      qsa('[data-go]', this).forEach((b) => b.setAttribute('aria-current', parseInt(b.dataset.go, 10) === this.index ? 'true' : 'false'));
      const c = qs('[data-counter]', this);
      if (c) c.textContent = `${this.index + 1} / ${this.slides.length}`;
      const p = qs('[data-prev]', this), n = qs('[data-next]', this);
      if (p) p.disabled = this.index === 0;
      if (n) n.disabled = this.index === this.slides.length - 1;
    }
    bindZoom() {
      const box = qs('[data-lightbox]');
      if (!box) return;
      const img = qs('[data-lightbox-img]', box);
      const close = () => { box.hidden = true; document.body.classList.remove('overflow-hidden'); if (this._z) this._z.focus(); };
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        this._z = z;
        img.src = z.dataset.zoom;
        img.alt = (qs('img', z) || {}).alt || '';
        box.hidden = false;
        document.body.classList.add('overflow-hidden');
        qs('[data-lightbox-close]', box).focus();
      });
      if (!box._bound) {
        box._bound = true;
        box.addEventListener('click', (e) => { if (e.target === box || e.target.closest('[data-lightbox-close]')) close(); });
        box.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      }
    }
  });

  /* ==========================================================================
     Sticky add to cart
     ========================================================================== */
  customElements.define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const target = qs('[data-main-buy]');
      if (!target) return;
      const btn = qs('[data-sticky-add]', this);
      btn.addEventListener('click', () => {
        const form = document.getElementById(this.dataset.formId);
        const main = form && qs('[data-add-button]', form);
        if (main && !main.disabled) submitAdd(form, btn);
      });
      const footer = qs('.site-footer');
      let pastBuy = false, footerVisible = false;
      const apply = () => {
        const show = pastBuy && !footerVisible;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        btn.tabIndex = show ? 0 : -1;
        document.body.classList.toggle('has-sticky-atc', show);
      };
      new IntersectionObserver(([en]) => { pastBuy = !en.isIntersecting && en.boundingClientRect.top < 0; apply(); }).observe(target);
      if (footer) new IntersectionObserver(([en]) => { footerVisible = en.isIntersecting; apply(); }).observe(footer);
    }
  });

  /* ==========================================================================
     Header: sticky/transparent state
     ========================================================================== */
  customElements.define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.classList.toggle('is-scrolled', window.scrollY > 24);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  });

  customElements.define('announcement-rotator', class extends HTMLElement {
    connectedCallback() {
      const slides = qsa('.announcement__slide', this);
      if (slides.length < 2 || reduceMotion) return;
      let i = 0;
      const tick = () => {
        slides[i].classList.remove('is-active'); slides[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active'); slides[i].removeAttribute('aria-hidden');
      };
      let timer = setInterval(tick, (parseInt(this.dataset.interval, 10) || 5) * 1000);
      this.addEventListener('mouseenter', () => clearInterval(timer));
      this.addEventListener('mouseleave', () => { timer = setInterval(tick, (parseInt(this.dataset.interval, 10) || 5) * 1000); });
    }
  });

  /* ==========================================================================
     Search modal + predictive search
     ========================================================================== */
  customElements.define('search-modal', class extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.search-modal__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') trapFocus(this.panel, e);
      });
    }
    open(opener) {
      this._opener = opener;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => qs('input[type="search"]', this).focus(), 60);
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (this._opener) this._opener.focus({ preventScroll: true });
    }
  });

  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-results]', this);
      this.suggestions = qs('[data-suggestions]', this);
      this.cache = {};
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => this.nav(e));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.restore(); return; }
      if (this.cache[q]) { this.show(this.cache[q]); return; }
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const params = new URLSearchParams({ q, section_id: 'predictive-search', 'resources[type]': 'product,collection,page,article', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body' });
      try {
        const res = await fetch(`${C.routes.predictiveSearch}?${params}`, { signal: this.ctrl.signal });
        if (!res.ok) throw new Error();
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = qs('#shopify-section-predictive-search', doc);
        const out = inner ? inner.innerHTML : '';
        this.cache[q] = out;
        this.show(out);
      } catch (e) { if (e.name !== 'AbortError') this.restore(); }
    }
    show(html) {
      this.results.innerHTML = html;
      this.input.setAttribute('aria-expanded', 'true');
    }
    restore() {
      this.results.innerHTML = '';
      if (this.suggestions) this.results.appendChild(this.suggestions);
      this.input.setAttribute('aria-expanded', 'false');
    }
    nav(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const opts = qsa('[role="option"] a', this.results);
      if (!opts.length) return;
      let i = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') { if (i > -1) { e.preventDefault(); opts[i].click(); } return; }
      e.preventDefault();
      if (i > -1) opts[i].setAttribute('aria-selected', 'false');
      i = e.key === 'ArrowDown' ? (i + 1) % opts.length : (i - 1 + opts.length) % opts.length;
      opts[i].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opts[i].parentElement.id);
      opts[i].scrollIntoView({ block: 'nearest' });
    }
  });

  /* ==========================================================================
     Tabs (format selector)
     ========================================================================== */
  customElements.define('tabs-component', class extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          let n = null;
          if (e.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = this.tabs.length - 1;
          if (n !== null) { e.preventDefault(); this.select(n); this.tabs[n].focus(); }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (!p) return;
        p.hidden = !on;
        if (on) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
      });
    }
  });

  /* ==========================================================================
     Collection filters
     ========================================================================== */
  customElements.define('facet-form', class extends HTMLElement {
    connectedCallback() {
      const form = qs('form', this);
      form.addEventListener('change', debounce((e) => {
        if (e.target.type === 'number') return;
        this.submit(form);
      }, 150));
      form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
      document.addEventListener('click', (e) => {
        qsa('details.facet[open]', this).forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
      });
    }
    submit(form) {
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      window.location.search = params.toString();
    }
  });

  /* ==========================================================================
     Product recommendations
     ========================================================================== */
  customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      const load = () => fetch(this.dataset.url).then((r) => r.text()).then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('product-recommendations', doc);
        if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; revealIn(this); }
      }).catch(() => {});
      new IntersectionObserver((entries, obs) => { if (entries[0].isIntersecting) { obs.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  });

  /* ==========================================================================
     Reveal on scroll + subtle parallax
     ========================================================================== */
  let revealObserver = null;
  const revealIn = (root = document) => {
    const els = qsa('.reveal:not(.is-visible), .reveal-img:not(.is-visible)', root);
    if (!document.body.classList.contains('animations-on') || reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); revealObserver.unobserve(en.target); } });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    els.forEach((el) => revealObserver.observe(el));
  };
  revealIn();

  const parallax = qsa('[data-parallax]');
  if (parallax.length && !reduceMotion && document.body.classList.contains('animations-on')) {
    let ticking = false;
    const run = () => {
      parallax.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const p = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        const img = el.querySelector('img');
        if (img) img.style.transform = `translate3d(0, ${(p * -40).toFixed(1)}px, 0) scale(1.08)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(run); } }, { passive: true });
    run();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => revealIn(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target.closest('[role="tabpanel"]');
    if (panel) {
      const tabs = panel.closest('tabs-component');
      const i = qsa('[role="tabpanel"]', tabs).indexOf(panel);
      if (tabs && i > -1) tabs.select(i);
    }
    if (e.target.matches('details')) e.target.open = true;
  });
})();
